import clsx from 'clsx';
import React, { CSSProperties, forwardRef, ReactNode } from 'react';
import { isEmptyObject } from '@srcDS/utils/assertions';
import styles from './Text.module.scss';

type ElementType = 'div' | 'p' | 'span' | 'small' | 'code' | 'h2' | 'h3' | 'h4';

interface TextProps {
  as?: ElementType;
  size?: 's' | 'xxxs' | 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl';
  weight?: 'normal' | 'light' | 'semibold' | 'bold' | 'regular';
  children: ReactNode;
  className?: string;
  align?: 'left' | 'start' | 'center' | 'end' | 'right';
  textTransform?: 'uppercase' | 'lowercase' | 'capitalize' | 'firstletter' | 'initial' | 'none';
  inlineStyles?: CSSProperties;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  variables?: Record<string, unknown>;
  [key: string]: any;
}

export const Text = forwardRef<HTMLElement, TextProps>(
  (
    {
      as = 'p',
      size = 'md',
      weight = 'normal',
      children,
      className = '',
      align = 'start',
      textTransform = 'none',
      inlineStyles,
      onClick,
      variables = {},
    },
    ref,
  ) => {
    const Element = as;

    const headingClass = clsx(
      {
        [` ${styles[`size_${size}`]}`]: size,
        [` ${styles[`weight_${weight}`]}`]: weight,
      },
      styles[`text_${textTransform}`],
      styles[`align_${align}`],
      className,
    );

    const transformChildren = (): ReactNode => {
      if (textTransform === 'firstletter' && typeof children === 'string') {
        return children.slice(0, 1).toUpperCase() + children.slice(1);
      }
      return children;
    };

    const fontWeights: Record<string, number> = {
      light: 300,
      normal: 400,
      semibold: 500,
      bold: 600,
      regular: 400,
    };

    const mergedStyles = {
      ...inlineStyles,
      '--default-font-weight': fontWeights[weight],
      '--default-text-transform': textTransform,
    };

    return (
      <Element
        style={!isEmptyObject(variables) ? { ...mergedStyles, ...variables } : mergedStyles}
        ref={ref as any}
        className={headingClass}
        onClick={onClick}
      >
        {transformChildren()}
      </Element>
    );
  },
);

Text.displayName = 'Text';
